import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChatSession, ChatSessionResponse } from "../models/ChatSession";
import useMessages, { MessageValues } from "./useMessages";
import {
  AiTaskStatus,
  JobRecommendTaskDTO,
  ResumeSummarizeTaskDTO,
  UserStatus,
} from "../models/Message";

export interface ChatSessionValues {
  chatSessions: ChatSession[] | null;
  activeSession: ChatSession | null;
  switchActiveSession: (
    newSession: ChatSession | null,
    isNewlyTakenOver: boolean,
  ) => void;
  messageValues: MessageValues;
  isAssigned: boolean;
  changeSessionAssignment: (assignment: boolean) => void;
  takeOverUnassignedSession: (chatSession: ChatSession) => Promise<void>;
  giveUpAssignedSession: (chatSessionId: string) => Promise<void>;
  sessionsLoading: boolean;
  sessionUserStatuses: Map<string, UserStatus>;
  unreadCounts: Map<string, number>;
  lastMessages: Map<string, string>;
  setJobRecommendTaskStatusDTO: (statusDto: JobRecommendTaskDTO) => void;
  setResumeSummarizeTaskStatusDTO: (statusDto: ResumeSummarizeTaskDTO) => void;
  incrementPageNum: () => void;
  initialChatSessionsLoaded: boolean;
  updateHasJobAlert: (chatSessionId: string, hasJobAlert: boolean) => void;
}

const useChatSessions = (
  changeConnectionStatus: (status: string) => void,
  setJobRecommendTaskStatusDTO: (statusDto: JobRecommendTaskDTO) => void,
  setResumeSummarizeTaskStatusDTO: (statusDto: ResumeSummarizeTaskDTO) => void,
): ChatSessionValues => {
  const [chatSessions, setChatSessions] = useState<ChatSession[] | null>(null);
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const [isAssigned, setIsAssigned] = useState<boolean>(true);
  const [sessionsLoading, setSessionsLoading] = useState<boolean>(true);
  const [pageNum, setPageNum] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [initialChatSessionsLoaded, setInitialChatSessionsLoaded] =
    useState(false);
  const [tabChanged, setTabChanged] = useState<boolean>(false);
  const [sessionUserStatuses, setSessionUserStatuses] = useState<
    Map<string, UserStatus>
  >(new Map());
  const [unreadCounts, setUnreadCounts] = useState<Map<string, number>>(
    new Map(),
  );
  const [lastMessages, setLastMessages] = useState<Map<string, string>>(
    new Map(),
  );

  const unreadCountsRef = useRef(unreadCounts);

  const lastMessagesRef = useRef(lastMessages);

  const activeSessionIdRef = useRef(activeSession?.sessionId);

  useEffect(() => {
    activeSessionIdRef.current = activeSession?.sessionId;
  }, [activeSession]);

  useEffect(() => {
    unreadCountsRef.current = unreadCounts;
  }, [unreadCounts]);

  useEffect(() => {
    lastMessagesRef.current = lastMessages;
  }, [lastMessages]);

  const incrementUnreadCount = (sessionId: string) => {
    const previousUnreadCount = unreadCountsRef.current.get(sessionId);
    if (previousUnreadCount == null) return;
    const newMap = new Map(
      unreadCountsRef.current.set(sessionId, previousUnreadCount + 1),
    );
    setUnreadCounts(newMap);
  };

  const updateJobRecommendTaskStatus = (
    jobRecommendTaskDTO: JobRecommendTaskDTO,
  ) => {
    setJobRecommendTaskStatusDTO(jobRecommendTaskDTO);
  };

  const updateHasJobAlert = (chatSessionId: string, hasJobAlert: boolean) => {
    const chatSessionsCopy = [...chatSessions!];
    const chatSession = chatSessionsCopy?.find(
      (chatSession) => chatSession.sessionId === chatSessionId,
    );
    if (chatSession == null) return;
    chatSession.hasJobAlert = hasJobAlert;
    setChatSessions(chatSessionsCopy);
  };

  const updateResumeSummarizeTaskStatus = (
    newStatus: AiTaskStatus,
    taskId: string,
  ) => {
    setResumeSummarizeTaskStatusDTO({
      status: newStatus,
      resumeSummarizeTaskId: taskId,
    });
  };

  const updateLastMessage = (sessionId: string, newMessage: string) => {
    const newMap = new Map(lastMessagesRef.current.set(sessionId, newMessage));
    setLastMessages(newMap);
  };

  const changeUserStatuses = (newStatus: UserStatus, sessionId: string) => {
    const newMap = new Map(sessionUserStatuses.set(sessionId, newStatus));
    setSessionUserStatuses(newMap);
  };

  const messageValues = useMessages(
    activeSession,
    chatSessions,
    activeSessionIdRef,
    incrementUnreadCount,
    updateLastMessage,
    changeConnectionStatus,
    changeUserStatuses,
    updateJobRecommendTaskStatus,
    updateResumeSummarizeTaskStatus,
    initialChatSessionsLoaded,
  );

  const fetchChatSessions = useCallback(async () => {
    if (pageNum === 1) {
      setSessionsLoading(true);
    }
    console.log(pageNum);
    const response = await fetchWithAuthentication(
      `/chat/sessions?sessionAssigned=${isAssigned}&pageNum=${pageNum}`,
      "GET",
      null,
    );
    const body: ChatSessionResponse = await response.json();
    setPageTotal(body.totalPageNumber);
    const newUnreadMap: Map<string, number> = new Map();
    const newLastMessageMap = new Map();
    body.chatSessions.forEach((chatSession) => {
      newUnreadMap.set(chatSession.sessionId, chatSession.unreadCount);
      newLastMessageMap.set(chatSession.sessionId, chatSession.lastMessage);
    });
    setUnreadCounts(new Map([...unreadCounts, ...newUnreadMap]));
    setLastMessages(new Map([...lastMessages, ...newLastMessageMap]));
    if (chatSessions != null && chatSessions.length > 0 && !tabChanged) {
      const newSubscriptions = messageValues.createSubscriptionDetails(
        body.chatSessions,
      );
      messageValues.stompClientValues.subscribeToTopics(newSubscriptions);
      setChatSessions((prev) => [...prev!, ...body.chatSessions]);
    } else {
      setChatSessions(body.chatSessions);
    }
    setSessionsLoading(false);
    setTabChanged(false);
    if (!initialChatSessionsLoaded) {
      setInitialChatSessionsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssigned, pageNum]);

  const changeSessionAssignment = (assigned: boolean) => {
    setTabChanged(true);
    setIsAssigned(assigned);
    switchActiveSession(null);
    setPageNum(1);
  };

  const switchActiveSession = (newSession: ChatSession | null) => {
    console.log("switchActiveSession called");
    if (newSession != null) {
      const previousUnreadCount = unreadCounts.get(newSession.sessionId);
      if (previousUnreadCount != null) {
        setUnreadCounts(
          new Map(
            unreadCounts.set(
              newSession.sessionId,
              Math.max(0, previousUnreadCount - 50),
            ),
          ),
        );
      }
    }
    setActiveSession(newSession);
  };

  const takeOverUnassignedSession = async (chatSession: ChatSession) => {
    setSessionsLoading(true);
    const response = await fetchWithAuthentication(
      "/chat/take-over",
      "POST",
      chatSession.sessionId,
    );
    if (response.ok) {
      setTabChanged(true);
      setPageNum(1);
      setIsAssigned(true);
      const json = await response.json();
      const adminUserId = json.adminUserId;
      setActiveSession({ ...chatSession, adminUserId: adminUserId });
    }
    setSessionsLoading(false);
  };

  const giveUpAssignedSession = async (chatSessionId: string) => {
    setSessionsLoading(true);
    const response = await fetchWithAuthentication(
      "/chat/give-up",
      "POST",
      chatSessionId,
    );
    if (response.ok) {
      setActiveSession(null);
    }
    await fetchChatSessions();
    setSessionsLoading(false);
  };

  const incrementPageNum = () => {
    if (pageNum < pageTotal) {
      setPageNum((prevState) => prevState + 1);
    }
  };

  useEffect(() => {
    fetchChatSessions()
      .then(() => {
        console.log("Chat sessions fetched successfully");
      })
      .catch((error) => {
        console.log("Chat sessions failed to fetch", error);
      });
  }, [isAssigned, fetchChatSessions, pageNum]);

  return {
    chatSessions,
    activeSession,
    switchActiveSession,
    messageValues,
    isAssigned,
    changeSessionAssignment,
    takeOverUnassignedSession,
    giveUpAssignedSession,
    sessionsLoading,
    sessionUserStatuses,
    unreadCounts,
    lastMessages: lastMessages,
    setJobRecommendTaskStatusDTO,
    setResumeSummarizeTaskStatusDTO,
    incrementPageNum,
    initialChatSessionsLoaded,
    updateHasJobAlert,
  };
};

export default useChatSessions;
